import axios from 'axios';
import moment from 'moment';
import 'moment/dist/locale/fr';

// Setup moment locale
moment.locale(window.config?.locale || 'en');

// Axios setup
axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

// Keep a reference to the boot-time CSRF <meta> for the few consumers that read it
// directly (Echo broadcasting auth, the OAuth authorize form). We deliberately do NOT
// pin it as axios's default `X-CSRF-TOKEN` header: that value is captured once at page
// load and goes stale the moment the session's CSRF token rotates (e.g. a re-login in
// another tab that shares the session cookie). A stale header is worse than none —
// Passport's /api cookie guard prefers `X-CSRF-TOKEN` over `X-XSRF-TOKEN`, so a stale
// pin makes every /api call 401 while the session is still alive. Instead we let axios
// send `X-XSRF-TOKEN` automatically from the `XSRF-TOKEN` cookie, which Laravel
// refreshes on every web response (including /heartbeat) — so it can never go stale.
const csrfToken = document.head.querySelector('meta[name="csrf-token"]');
window.csrf_token = csrfToken;
if (!csrfToken) {
    console.error('CSRF token not found: https://laravel.com/docs/csrf#csrf-x-csrf-token');
}

// Centralized 401 handling for the org app. IMPORTANT: a 401 alone does NOT mean
// the user is logged out — an endpoint can return 401 for its own reasons while the
// session is perfectly alive. So we never redirect on a 401 directly. Instead we
// probe /heartbeat (web group): if it answers 204 the session is alive and we just
// surface the original error (optionally retrying once, in case the token cookie was
// stale); only if /heartbeat itself returns 401 — a genuinely dead session — do we
// send the user back to login, preserving where they were. Portal requests use a
// bearer token and manage their own auth, so they are left untouched.
let redirectingToLogin = false;

// One-shot self-heal for a session ↔ Passport-cookie CSRF divergence. The org SPA
// authenticates /api/* through Passport's `laravel_token` cookie guard, which checks
// the request's CSRF token against the one baked into that cookie. If the session
// CSRF token rotates without a full page load (e.g. a re-login in another tab that
// shares the session cookie), the SPA's boot-time CSRF token goes stale and every
// /api write 401s — while /heartbeat (session guard, no CSRF check) still answers 204.
// A single full reload re-syncs both the CSRF <meta> and the laravel_token cookie.
// Bounded to one reload per minute so a genuinely stuck state cannot loop.
const AUTH_RESYNC_KEY = 'authResyncReloadedAt';
function recentlyResynced() {
    try {
        const ts = Number(sessionStorage.getItem(AUTH_RESYNC_KEY));
        return !!ts && Date.now() - ts < 60000;
    } catch (e) {
        return false;
    }
}
function markResynced() {
    try {
        sessionStorage.setItem(AUTH_RESYNC_KEY, String(Date.now()));
    } catch (e) {
        // ignore storage errors
    }
}

// Shared in-flight session probe: if several requests 401 at once (e.g. on boot),
// they all await a single /heartbeat instead of firing one each — no request fan-out.
let sessionProbe = null;
function probeSessionAlive() {
    if (!sessionProbe) {
        sessionProbe = axios
            .get('/heartbeat')
            .then(r => r.status === 204)
            .catch(e => {
                if (e?.response?.status === 401) {
                    return false; // session is genuinely gone
                }
                throw e; // network/other — let the caller decide (never force a logout)
            })
            .finally(() => {
                sessionProbe = null;
            });
    }
    return sessionProbe;
}

const LOSS_FIELDS = ['loss_type', 'loss_reason', 'loss_competitor', 'loss_comment'];

/**
 * Open the loss window for a refused save, then send that save again.
 *
 * Closed without confirming, the refusal goes back to the screen marked as
 * dismissed, so AxiosError stays quiet: the person chose not to lose the deal,
 * nothing failed. The replay carries the loss just recorded in place of
 * whatever loss fields the screen had sent — a full-record form sends them back
 * empty, which would read as clearing the reason.
 */
async function askLossThenReplay(error) {
    const { response, config } = error;
    const { opportunity_id: id, pipeline_stage_id: stageId } = response.data.loss_required;
    const api = String(config.url).match(/^.*?\/api\/[^/]+/)?.[0];
    const { askLoss } = await import('@modules/Opportunities/Resources/js/askLoss');

    let opportunity = { id };
    try {
        opportunity = (await axios.get(`${api}/opportunities/${id}`)).data;
    } catch {
        // The window only needs the id; the name in its header is a courtesy.
    }

    const saved = await askLoss(opportunity, { id: stageId, is_lost: true });
    if (!saved) {
        response.data.loss_dismissed = true;

        return Promise.reject(error);
    }

    if (typeof config.data === 'string') {
        try {
            const payload = JSON.parse(config.data);
            LOSS_FIELDS.filter(field => field in payload).forEach(field => (payload[field] = saved[field]));
            config.data = JSON.stringify(payload);
        } catch {
            // Not JSON: sent again as it was.
        }
    }

    return axios(config);
}

axios.interceptors.response.use(
    response => response,
    async error => {
        const response = error?.response;
        const config = error?.config;

        // Sudo mode: the server refused a sensitive action until the password is
        // proven again. Prompt here rather than in every caller, then replay the
        // original request so the flow is uninterrupted.
        if (response?.status === 423 && response.data?.password_confirmation_required && !config?._retriedAfterSudo) {
            config._retriedAfterSudo = true;

            let password;
            try {
                password = await Modal.prompt(
                    i18n.t('Confirm your password'),
                    i18n.t('Enter your current password to continue.'),
                    { type: 'password', confirmText: i18n.t('Confirm') }
                );
            } catch (e) {
                return Promise.reject(error); // cancelled
            }

            await axios.post('/confirm-password', { password });

            return axios(config);
        }

        // A deal moved into a lost stage without saying why. Whatever screen sent
        // it — an Object Manager tab, one not written yet — the loss window opens
        // here, saves the stage with its reason, and the screen's own save is
        // replayed. The same shape as sudo mode above; see OpportunityLoss::refuse().
        if (response?.status === 422 && response.data?.loss_required && !config?._retriedAfterLoss) {
            config._retriedAfterLoss = true;

            return askLossThenReplay(error);
        }

        // Suspended for non-payment: every screen but billing is closed, so the
        // application goes to the one page that says why and what to do.
        if (response?.status === 423 && response.data?.error === 'organization_suspended') {
            const route = window.router?.currentRoute?.value;

            if (route?.params?.org && route.name !== 'organization-suspended') {
                window.router.push({ name: 'organization-suspended', params: { org: route.params.org } });
            }

            return Promise.reject(error);
        }

        if (!response || !config || response.status !== 401) {
            return Promise.reject(error);
        }

        const url = config.url || '';
        const isPortal = url.includes('/api/portal/') || window.location.pathname.startsWith('/portal');
        const isAuthEndpoint = url.includes('/heartbeat') || url.endsWith('/login');
        const onLoginPage = window.location.pathname === '/login';

        if (isPortal || isAuthEndpoint || onLoginPage || config._retriedAfter401) {
            return Promise.reject(error);
        }

        config._retriedAfter401 = true;

        // Probe whether the session itself is dead (deduped across concurrent 401s).
        let sessionAlive = false;
        try {
            sessionAlive = await probeSessionAlive();
        } catch (e) {
            return Promise.reject(error); // network/other — stay put, never force a logout
        }

        if (sessionAlive) {
            // Session is fine, so this is NOT a logout — never redirect to login here.
            // The cookie may have just been refreshed by the /heartbeat probe, so retry
            // once. If it still 401s while the session is alive, the session and the
            // Passport /api cookie guard have diverged (almost always a stale CSRF
            // token). A single full reload re-syncs the CSRF <meta> and the
            // laravel_token cookie; bounded to one reload/minute to avoid loops.
            try {
                return await axios(config);
            } catch (e) {
                if (e?.response?.status === 401 && !recentlyResynced()) {
                    markResynced();
                    window.location.reload();
                }
                return Promise.reject(e);
            }
        }

        if (!redirectingToLogin) {
            redirectingToLogin = true;
            try {
                localStorage.setItem('redirectAfterLogin', window.location.pathname + window.location.search);
            } catch (e) {
                // ignore storage errors
            }
            window.location = '/login';
        }

        return Promise.reject(error);
    }
);

// Lazy-load Echo (Reverb/Pusher) — only initialized when first needed
let echoInstance = null;
async function initEcho() {
    if (echoInstance) return echoInstance;

    const { default: Echo } = await import('laravel-echo');
    const { default: Pusher } = await import('pusher-js');
    window.Pusher = Pusher;

    // Broadcasting channel auth. The main domain authenticates through the session
    // guard (/broadcasting/auth) — robust, same guard as /heartbeat. Custom domains
    // share no session with the platform, so they authenticate through Passport on the
    // api route (/api/broadcasting/auth). Both carry the CSRF token below.
    const authHeaders = { 'X-CSRF-TOKEN': csrfToken?.content };
    const broadcastAuthEndpoint = window.config?.customDomain ? '/api/broadcasting/auth' : '/broadcasting/auth';

    const reverb = window.config?.reverb || {};

    echoInstance = new Echo({
        broadcaster: 'reverb',
        key: reverb.key,
        wsHost: window.location.hostname,
        wsPort: reverb.port ?? 80,
        wssPort: reverb.port ?? 443,
        wsPath: reverb.path || '',
        forceTLS: (reverb.scheme ?? 'https') === 'https',
        enabledTransports: ['ws', 'wss'],
        authEndpoint: broadcastAuthEndpoint,
        auth: {
            headers: authHeaders
        }
    });

    window.Echo = echoInstance;
    return echoInstance;
}

// Lazy-load highlight.js — only initialized when first needed
let hljsInstance = null;
async function initHljs() {
    if (hljsInstance) return hljsInstance;
    const { default: hljs } = await import('highlight.js/lib/core');
    const { default: json } = await import('highlight.js/lib/languages/json');
    hljs.registerLanguage('javascript', json);
    hljsInstance = hljs;
    window.hljs = hljs;
    return hljs;
}

// Global exports
window.axios = axios;
window.moment = moment;
window.initEcho = initEcho;
window.initHljs = initHljs;

// Export for ES module usage
export { axios, moment, initEcho, initHljs };
